import { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import {
  FiEye,
  FiEyeOff,
  FiLock,
  FiMail,
  FiAlertCircle,
  FiCheckCircle,
} from "react-icons/fi";
import img from "../../assets/LogoCabecalho.png";
import imgDark from "../../assets/LogoCabecalhoBranca.png";
import Load from "../../components/ui/Load";
import Modal from "../../tailadmin/components/ui/Modal";
import studentsService from "../../services/students";
import { toast } from "react-toastify";
import InputDefault from "../../components/ui/InputDefault";

const LoginPage = () => {
  const { login } = useAuth();
  const [load, setLoad] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);

  // Forgot Password State
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async () => {
    setError("");

    if (!validateEmail(email)) {
      setError("Por favor, insira um email válido");
      return;
    }

    if (password.length < 6) {
      setError("A senha deve ter pelo menos 6 caracteres");
      return;
    }

    setLoad(true);
    try {
      await login(email, password);
    } catch (err) {
      setError("Email ou senha incorretos. Tente novamente.");
    } finally {
      setLoad(false);
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    setError("");
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    setError("");
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && canSubmit) {
      handleSubmit();
    }
  };

  const handleResetPassword = async () => {
    if (!validateEmail(resetEmail)) {
      toast.error("Por favor, insira um email válido");
      return;
    }

    setResetLoading(true);
    try {
      const success = await studentsService.resetPasswordByEmail(resetEmail);
      if (success) {
        setResetSuccess(true);
        toast.success(
          "Email de recuperação enviado! Verifique sua caixa de entrada.",
        );
        setTimeout(() => {
          setShowForgotPassword(false);
          setResetEmail("");
          setResetSuccess(false);
        }, 2000);
      } else {
        toast.error("Erro ao enviar email. Verifique se o email está correto.");
      }
    } catch (error) {
      toast.error("Ocorreu um erro. Tente novamente mais tarde.");
    } finally {
      setResetLoading(false);
    }
  };

  const isEmailValid = email && validateEmail(email);
  const isPasswordValid = password.length >= 6;
  const canSubmit = isEmailValid && isPasswordValid && !load;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-boxdark-2 dark:via-boxdark dark:to-boxdark-2 px-4 py-8 transition-colors duration-500">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary/5 rounded-full blur-3xl animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-400/5 rounded-full blur-3xl animate-blob animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-indigo-400/5 rounded-full blur-3xl animate-blob animation-delay-4000"></div>
      </div>

      <div className="relative w-full max-w-md">
        {/* Card with entrance animation */}
        <div className="rounded-2xl border border-stroke bg-white/95 backdrop-blur-xl shadow-2xl dark:border-strokedark dark:bg-boxdark/95 transition-all duration-300 animate-slideUp">
          <div className="px-6 py-10 sm:px-10">
            {/* Logo with fade-in animation */}
            <div className="flex flex-col items-center mb-8 animate-fadeIn">
              <div className="relative mb-6">
                <div className="absolute inset-0 bg-primary/10 blur-xl rounded-full animate-pulse"></div>
                <img
                  src={img}
                  alt="Logo"
                  className="relative w-32 dark:hidden transition-all duration-300 hover:scale-105"
                />
                <img
                  src={imgDark}
                  alt="Logo"
                  className="relative w-32 hidden dark:block transition-all duration-300 hover:scale-105"
                />
              </div>
              <h1 className="text-3xl font-bold text-black dark:text-white bg-gradient-to-r from-primary to-purple-600 bg-clip-text text-transparent">
                Bem-vindo!
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                Entre com suas credenciais para continuar
              </p>
            </div>

            {/* Error Message with animation */}
            {error && (
              <div className="mb-6 p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 animate-shake shadow-sm">
                <div className="flex items-center gap-3">
                  <FiAlertCircle
                    className="text-red-500 flex-shrink-0"
                    size={20}
                  />
                  <p className="text-sm text-red-600 dark:text-red-400">
                    {error}
                  </p>
                </div>
              </div>
            )}

            {/* Form Fields */}
            <div className="space-y-5">
              {/* Email */}
              <div
                className="animate-slideInLeft"
                style={{ animationDelay: "0.1s" }}
              >
                <label className="block mb-2.5 text-sm font-semibold text-black dark:text-white">
                  Email
                </label>
                <div className="relative group">
                  <div
                    className={`absolute inset-0 rounded-xl bg-gradient-to-r from-primary/20 to-purple-500/20 opacity-0 group-hover:opacity-100 blur transition-opacity duration-300 ${
                      emailTouched && isEmailValid ? "opacity-50" : ""
                    }`}
                  ></div>
                  <div className="relative">
                    <FiMail
                      className={`absolute left-4 top-1/2 -translate-y-1/2 transition-all duration-300 ${
                        emailTouched && !isEmailValid
                          ? "text-red-400 animate-wiggle"
                          : emailTouched && isEmailValid
                            ? "text-green-500"
                            : "text-gray-400 group-hover:text-primary"
                      }`}
                      size={20}
                    />
                    {emailTouched && isEmailValid && (
                      <FiCheckCircle
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-green-500 animate-scaleIn"
                        size={18}
                      />
                    )}
                    <input
                      type="email"
                      placeholder="seu@email.com"
                      autoComplete="email"
                      className={`w-full rounded-xl border bg-white/50 dark:bg-form-input/50 py-3.5 pl-12 pr-12 text-sm outline-none transition-all duration-300
                        ${
                          emailTouched && !isEmailValid
                            ? "border-red-300 dark:border-red-700 focus:border-red-500 focus:ring-4 focus:ring-red-500/10 shadow-red-100/50 dark:shadow-red-900/50"
                            : emailTouched && isEmailValid
                              ? "border-green-300 dark:border-green-700 focus:border-green-500 focus:ring-4 focus:ring-green-500/10 shadow-green-100/50 dark:shadow-green-900/50"
                              : "border-stroke dark:border-form-strokedark focus:border-primary focus:ring-4 focus:ring-primary/10 hover:border-primary/50"
                        }
                        shadow-sm hover:shadow-md
                      `}
                      value={email}
                      onChange={handleEmailChange}
                      onBlur={() => setEmailTouched(true)}
                      onKeyPress={handleKeyPress}
                      aria-label="Email"
                      aria-invalid={emailTouched && !isEmailValid}
                    />
                  </div>
                </div>
                {emailTouched && !isEmailValid && email && (
                  <p className="mt-2 text-xs text-red-500 dark:text-red-400 flex items-center gap-1 animate-slideDown">
                    <FiAlertCircle size={12} />
                    Email inválido
                  </p>
                )}
              </div>

              {/* Password */}
              <div
                className="animate-slideInLeft"
                style={{ animationDelay: "0.2s" }}
              >
                <label className="block mb-2.5 text-sm font-semibold text-black dark:text-white">
                  Senha
                </label>
                <div className="relative group">
                  <div
                    className={`absolute inset-0 rounded-xl bg-gradient-to-r from-primary/20 to-purple-500/20 opacity-0 group-hover:opacity-100 blur transition-opacity duration-300 ${
                      passwordTouched && isPasswordValid ? "opacity-50" : ""
                    }`}
                  ></div>
                  <div className="relative">
                    <FiLock
                      className={`absolute left-4 top-1/2 -translate-y-1/2 transition-all duration-300 ${
                        passwordTouched && !isPasswordValid
                          ? "text-red-400 animate-wiggle"
                          : passwordTouched && isPasswordValid
                            ? "text-green-500"
                            : "text-gray-400 group-hover:text-primary"
                      }`}
                      size={20}
                    />
                    {passwordTouched && isPasswordValid && (
                      <FiCheckCircle
                        className="absolute right-12 top-1/2 -translate-y-1/2 text-green-500 animate-scaleIn"
                        size={18}
                      />
                    )}
                    <input
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      autoComplete="current-password"
                      className={`w-full rounded-xl border bg-white/50 dark:bg-form-input/50 py-3.5 pl-12 pr-12 text-sm outline-none transition-all duration-300
                        ${
                          passwordTouched && !isPasswordValid
                            ? "border-red-300 dark:border-red-700 focus:border-red-500 focus:ring-4 focus:ring-red-500/10 shadow-red-100/50 dark:shadow-red-900/50"
                            : passwordTouched && isPasswordValid
                              ? "border-green-300 dark:border-green-700 focus:border-green-500 focus:ring-4 focus:ring-green-500/10 shadow-green-100/50 dark:shadow-green-900/50"
                              : "border-stroke dark:border-form-strokedark focus:border-primary focus:ring-4 focus:ring-primary/10 hover:border-primary/50"
                        }
                        shadow-sm hover:shadow-md
                      `}
                      value={password}
                      onChange={handlePasswordChange}
                      onBlur={() => setPasswordTouched(true)}
                      onKeyPress={handleKeyPress}
                      aria-label="Senha"
                      aria-invalid={passwordTouched && !isPasswordValid}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-primary dark:hover:text-primary transition-all duration-300 focus:outline-none focus:text-primary hover:scale-110 active:scale-95"
                      aria-label={
                        showPassword ? "Ocultar senha" : "Mostrar senha"
                      }
                    >
                      {showPassword ? (
                        <FiEyeOff size={18} />
                      ) : (
                        <FiEye size={18} />
                      )}
                    </button>
                  </div>
                </div>
                {passwordTouched && !isPasswordValid && password && (
                  <p className="mt-2 text-xs text-red-500 dark:text-red-400 flex items-center gap-1 animate-slideDown">
                    <FiAlertCircle size={12} />A senha deve ter pelo menos 6
                    caracteres
                  </p>
                )}
              </div>

              <div
                className="flex justify-end animate-slideInLeft"
                style={{ animationDelay: "0.3s" }}
              >
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm font-semibold text-primary hover:text-primary/80 dark:text-primary dark:hover:text-primary/80 transition-all duration-200 hover:underline underline-offset-2"
                >
                  Esqueci minha senha
                </button>
              </div>

              {/* Button */}
              <button
                onClick={handleSubmit}
                disabled={!canSubmit}
                className={`w-full flex items-center justify-center gap-2 rounded-xl py-4 text-sm font-bold text-white transition-all duration-300 animate-slideInLeft shadow-lg hover:shadow-xl
                  ${
                    canSubmit
                      ? "bg-gradient-to-r from-primary to-purple-600 hover:from-primary/90 hover:to-purple-600/90 active:scale-[0.98] hover:-translate-y-0.5"
                      : "bg-gray-300 dark:bg-gray-700 cursor-not-allowed opacity-60"
                  }
                `}
                style={{ animationDelay: "0.4s" }}
                aria-label="Entrar"
              >
                {load ? (
                  <>
                    <Load className="w-5 h-5 animate-spin" />
                    <span>Entrando...</span>
                  </>
                ) : (
                  <>
                    <span>Entrar</span>
                    <svg
                      className="w-4 h-4 transition-transform group-hover:translate-x-1"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 7l5 5m0 0l-5 5m5-5H6"
                      />
                    </svg>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
          20%, 40%, 60%, 80% { transform: translateX(5px); }
        }
        
        @keyframes blob {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }
        
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        @keyframes wiggle {
          0%, 100% { transform: translateX(0) translateY(-50%); }
          25% { transform: translateX(-3px) translateY(-50%); }
          75% { transform: translateX(3px) translateY(-50%); }
        }
        
        .animate-shake {
          animation: shake 0.5s ease-in-out;
        }
        
        .animate-blob {
          animation: blob 7s infinite;
        }
        
        .animate-slideUp {
          animation: slideUp 0.6s ease-out;
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.8s ease-out;
        }
        
        .animate-slideInLeft {
          animation: slideInLeft 0.5s ease-out;
          animation-fill-mode: both;
        }
        
        .animate-slideDown {
          animation: slideDown 0.3s ease-out;
        }
        
        .animate-scaleIn {
          animation: scaleIn 0.3s ease-out;
        }
        
        .animate-wiggle {
          animation: wiggle 0.3s ease-in-out;
        }
        
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>

      {/* Forgot Password Modal */}
      <Modal
        isOpen={showForgotPassword}
        onClose={() => {
          setShowForgotPassword(false);
          setResetEmail("");
          setResetSuccess(false);
        }}
        title="Recuperar Senha"
        className="!max-w-md"
      >
        <div className="space-y-6 p-4">
          {!resetSuccess ? (
            <>
              <div className="flex items-start gap-3 p-4 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                <FiMail
                  className="text-primary flex-shrink-0 mt-0.5"
                  size={20}
                />
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Digite seu email abaixo para receber as instruções de
                  recuperação de senha.
                </p>
              </div>

              <InputDefault
                label="Email"
                placeholder="seu@email.com"
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
              />

              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={() => {
                    setShowForgotPassword(false);
                    setResetEmail("");
                  }}
                  className="px-5 py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 transition-all duration-200 hover:shadow-md active:scale-95"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleResetPassword}
                  disabled={
                    resetLoading || !resetEmail || !validateEmail(resetEmail)
                  }
                  className={`px-5 py-2.5 text-sm font-semibold text-white rounded-xl transition-all duration-200 flex items-center gap-2 hover:shadow-lg active:scale-95
                    ${
                      resetLoading || !resetEmail || !validateEmail(resetEmail)
                        ? "bg-primary/50 cursor-not-allowed"
                        : "bg-gradient-to-r from-primary to-purple-600 hover:from-primary/90 hover:to-purple-600/90"
                    }`}
                >
                  {resetLoading ? (
                    <>
                      <Load className="w-4 h-4 animate-spin" />
                      <span>Enviando...</span>
                    </>
                  ) : (
                    <>
                      <FiMail size={16} />
                      <span>Enviar Email</span>
                    </>
                  )}
                </button>
              </div>
            </>
          ) : (
            <div className="py-8 text-center animate-scaleIn">
              <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-4">
                <FiCheckCircle className="text-green-500 text-3xl" />
              </div>
              <h3 className="text-lg font-semibold text-black dark:text-white mb-2">
                Email Enviado!
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Verifique sua caixa de entrada para recuperar sua senha.
              </p>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default LoginPage;
